import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getVideosByCategory, type Video } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const [activeCategory, setActiveCategory] = useState(slug || '');

  const videos = useMemo(() => {
    return getVideosByCategory(activeCategory);
  }, [activeCategory]);

  const activeCategoryInfo = getCategoryBySlug(activeCategory);

  if (!category) {
    return (
      <>
        <SEOHead
          title="频道不存在 - 蘑菇视频"
          description="抱歉，您访问的频道不存在。请浏览其他精彩频道。"
          keywords="蘑菇视频,短视频,频道"
          canonicalUrl="https://www.moguvideo.com/"
        />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">频道不存在</h1>
            <p className="text-sm text-foreground-600 mb-6">抱歉，您访问的频道不存在</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '蘑菇视频首页', item: 'https://www.moguvideo.com/' },
          { '@type': 'ListItem', position: 2, name: `${category.name}频道`, item: `https://www.moguvideo.com/category/${slug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `蘑菇视频${category.name}频道 - 精彩短视频在线观看`,
        description: `蘑菇视频${category.name}频道为您提供${category.description}。海量高清${category.name}短视频，随时随地在线观看。`,
        url: `https://www.moguvideo.com/category/${slug}`,
      },
    ],
  };

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.history.pushState({}, '', `/category/${newSlug}`);
  };

  return (
    <>
      <SEOHead
        title={`蘑菇视频${category.name}频道 - 精彩短视频在线观看 | 蘑菇视频App下载`}
        description={`蘑菇视频${category.name}频道为您提供${category.description}。海量高清${category.name}短视频，随时随地在线观看，下载蘑菇视频App发现更多精彩内容。`}
        keywords={`蘑菇视频,蘑菇视频${category.name},${category.name}短视频,${category.name}视频在线观看,蘑菇视频App下载`}
        canonicalUrl={`https://www.moguvideo.com/category/${slug}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-950 font-medium">{category.name}频道</span>
              </nav>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                {category.name}频道
              </h1>
              <p className="text-sm text-foreground-600">
                {category.description}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200 overflow-x-auto">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto flex items-center gap-1 py-3 flex-nowrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-950'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              {videos.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-video-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-600">该频道暂无视频内容，敬请期待</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {videos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/article/${video.id}`}
                      className="group block bg-background-100 rounded-xl overflow-hidden hover:bg-background-200 transition-colors duration-300 cursor-pointer"
                    >
                      <div className="relative w-full h-48 md:h-56 overflow-hidden">
                        <img
                          src={video.coverImage}
                          alt={video.title}
                          title={`${video.title} - 蘑菇视频`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-md">
                          {video.duration}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {video.title}
                        </h3>
                        <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
                          {video.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-foreground-500">{video.author}</span>
                          <div className="flex items-center gap-3 text-xs text-foreground-500">
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              {video.views > 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}